'use client';

import { useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Modal,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { OperationType } from '@polymarket/builder-relayer-client';
import { encodeFunctionData, erc20Abi } from 'viem';

import { usePolymarketClient, useSignInHandlers } from '@/hooks';
import { usePortfolioSummary, usePositions } from '@/services/query';
import { useAppDispatch, useAuth, useUI } from '@/store';
import { setSetupRequested } from '@/store/slices/auth.slice';
import { closePositionActionModal, openModal } from '@/store/slices/ui.slice';

import {
  CTF_CONTRACT_ADDRESS,
  USDC_E_CONTRACT_ADDRESS,
  USDC_E_DECIMALS,
} from '@/constants/tokens.constants';

function extractTxErrorMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  if (!err || typeof err !== 'object') return 'Failed to complete action';

  const e = err as any;
  const direct =
    e.errorMsg ||
    e.reason ||
    e.shortMessage ||
    e.message ||
    e.details ||
    e?.data?.message ||
    e?.response?.data?.errorMsg ||
    e?.response?.data?.message ||
    e?.response?.data?.error;

  if (typeof direct === 'string' && direct.trim()) return direct;

  if (Array.isArray(e?.errors)) {
    const firstMessage = e.errors.find((x: any) => typeof x?.message === 'string')?.message;
    if (firstMessage) return firstMessage;
  }

  try {
    const serialized = JSON.stringify(err);
    if (serialized && serialized !== '{}') return serialized;
  } catch {
    // ignore
  }

  return 'Transaction reverted';
}

function isFailedTxResult(result: any): boolean {
  if (!result) return false;
  const status = String(
    result?.status || result?.state || result?.receipt?.status || '',
  )?.toLowerCase();
  const explicitError =
    result?.errorMsg ||
    result?.error ||
    result?.reason ||
    result?.receipt?.error ||
    result?.receipt?.revertReason;
  return (
    Boolean(explicitError) ||
    status.includes('fail') ||
    status.includes('revert') ||
    status.includes('error') ||
    status === '0x0' ||
    status === '0'
  );
}

function isSuccessfulTxResult(result: any): boolean {
  if (!result) return false;
  const status = String(
    result?.status || result?.state || result?.receipt?.status || '',
  )?.toLowerCase();
  return (
    status.includes('success') ||
    status.includes('confirmed') ||
    status.includes('executed') ||
    status === '0x1' ||
    status === '1'
  );
}

export function PositionActionModal() {
  const dispatch = useAppDispatch();
  const { onLogin } = useSignInHandlers((error) => {
    if (error) console.error('Sign in error:', error);
  });
  const { positionActionModal } = useUI();
  const { isAuthenticated, isReadyToTrade, user } = useAuth();
  const { client: relayClient, isInitializing: isRelayInitializing } = usePolymarketClient();
  const { data: portfolioSummary } = usePortfolioSummary();
  // This modal is mounted on every route (ModalsOrchestrator sits in MainLayout) but
  // can only be opened from the market-detail page. Left ungated it polled positions
  // + open orders every 10s on every page for every signed-in user. It still renders
  // through MUI's close transition, so gating the query — not the component — keeps
  // the backdrop fade intact. On open the key matches TradingSidebar's own
  // usePositions(proxy, event?.id), so the data is already cached: no extra request.
  const { data: positions } = usePositions(
    user?.proxyWalletAddress,
    positionActionModal.eventId,
    500,
    0,
    { enabled: positionActionModal.isOpen },
  );

  const [amount, setAmount] = useState('0');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const action = positionActionModal.action;
  const balance = portfolioSummary?.cashBalance || 0;
  const amountNum = parseFloat(amount.replace(/[^0-9.]/g, '')) || 0;
  const yesTokenId = positionActionModal.tokenIds[0];
  const noTokenId = positionActionModal.tokenIds[1];

  const yesHolding = useMemo(() => {
    if (!positions || !yesTokenId) return 0;
    const pos = positions.find((p: any) => p.asset === yesTokenId);
    return pos ? parseFloat(pos.size) : 0;
  }, [positions, yesTokenId]);

  const noHolding = useMemo(() => {
    if (!positions || !noTokenId) return 0;
    const pos = positions.find((p: any) => p.asset === noTokenId);
    return pos ? parseFloat(pos.size) : 0;
  }, [positions, noTokenId]);

  const minMergeAmount = Math.min(yesHolding, noHolding);
  const isInvalidConditionId = !/^0x[a-fA-F0-9]{64}$/.test(positionActionModal.conditionId || '');

  const buttonText = useMemo(() => {
    if (isSubmitting) return 'Processing...';
    if (!isAuthenticated) return 'Log In';
    if (!isReadyToTrade || isRelayInitializing) return 'Loading...';
    if (action === 'split') return amountNum > balance ? 'Deposit' : 'Split Shares';
    return amountNum > minMergeAmount ? 'Insufficient Position' : 'Merge Shares';
  }, [
    isSubmitting,
    isAuthenticated,
    isReadyToTrade,
    isRelayInitializing,
    action,
    amountNum,
    balance,
    minMergeAmount,
  ]);

  const handleClose = () => {
    dispatch(closePositionActionModal());
    setAmount('0');
    setError(null);
    setIsSubmitting(false);
  };

  const handleSubmit = async () => {
    if (!isAuthenticated) {
      onLogin();
      return;
    }
    if (!user?.isReferredUser) {
      dispatch(openModal('referral'));
      return;
    }
    if (!isReadyToTrade) {
      dispatch(setSetupRequested(true));
      return;
    }
    if (action === 'split' && amountNum > balance) {
      dispatch(openModal('deposit'));
      return;
    }
    if (
      !relayClient ||
      isRelayInitializing ||
      isInvalidConditionId ||
      amountNum <= 0 ||
      (action === 'merge' && amountNum > minMergeAmount)
    ) {
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const collateralAmount = BigInt(Math.floor(amountNum * Math.pow(10, USDC_E_DECIMALS)));
      const parentCollectionId = `0x${'0'.repeat(64)}` as `0x${string}`;
      const partition = [1n, 2n];

      const ctfAbi = [
        {
          inputs: [
            { internalType: 'address', name: 'collateralToken', type: 'address' },
            { internalType: 'bytes32', name: 'parentCollectionId', type: 'bytes32' },
            { internalType: 'bytes32', name: 'conditionId', type: 'bytes32' },
            { internalType: 'uint256[]', name: 'partition', type: 'uint256[]' },
            { internalType: 'uint256', name: 'amount', type: 'uint256' },
          ],
          name: 'splitPosition',
          outputs: [],
          stateMutability: 'nonpayable',
          type: 'function',
        },
        {
          inputs: [
            { internalType: 'address', name: 'collateralToken', type: 'address' },
            { internalType: 'bytes32', name: 'parentCollectionId', type: 'bytes32' },
            { internalType: 'bytes32', name: 'conditionId', type: 'bytes32' },
            { internalType: 'uint256[]', name: 'partition', type: 'uint256[]' },
            { internalType: 'uint256', name: 'amount', type: 'uint256' },
          ],
          name: 'mergePositions',
          outputs: [],
          stateMutability: 'nonpayable',
          type: 'function',
        },
      ] as const;

      const txs =
        action === 'split'
          ? [
              {
                to: USDC_E_CONTRACT_ADDRESS,
                operation: OperationType.Call,
                data: encodeFunctionData({
                  abi: erc20Abi,
                  functionName: 'approve',
                  args: [CTF_CONTRACT_ADDRESS as `0x${string}`, collateralAmount],
                }),
                value: '0',
              },
              {
                to: CTF_CONTRACT_ADDRESS,
                operation: OperationType.Call,
                data: encodeFunctionData({
                  abi: ctfAbi,
                  functionName: 'splitPosition',
                  args: [
                    USDC_E_CONTRACT_ADDRESS as `0x${string}`,
                    parentCollectionId,
                    positionActionModal.conditionId as `0x${string}`,
                    partition,
                    collateralAmount,
                  ],
                }),
                value: '0',
              },
            ]
          : [
              {
                to: CTF_CONTRACT_ADDRESS,
                operation: OperationType.Call,
                data: encodeFunctionData({
                  abi: ctfAbi,
                  functionName: 'mergePositions',
                  args: [
                    USDC_E_CONTRACT_ADDRESS as `0x${string}`,
                    parentCollectionId,
                    positionActionModal.conditionId as `0x${string}`,
                    partition,
                    collateralAmount,
                  ],
                }),
                value: '0',
              },
            ];

      const response = await relayClient.execute(
        txs as any,
        action === 'split' ? 'Split Position' : 'Merge Positions',
      );
      const result = await response.wait();
      if (isFailedTxResult(result)) {
        throw result;
      }
      // Do not close modal unless relayer/on-chain result is explicitly successful.
      if (!isSuccessfulTxResult(result)) {
        throw {
          ...result,
          errorMsg: 'Transaction not confirmed as successful yet. Please check status and retry.',
        };
      }
      handleClose();
    } catch (err) {
      setError(extractTxErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={positionActionModal.isOpen}
      disableEscapeKeyDown={isSubmitting}
      onClose={(_, reason) => {
        if (isSubmitting || reason === 'backdropClick') return;
        handleClose();
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: { xs: 'calc(100% - 24px)', sm: 520 },
          maxWidth: '100%',
          p: 2.5,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          outline: 'none',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography sx={{ fontSize: '2rem', fontWeight: 700 }}>
            {action === 'split' ? 'Split shares' : 'Merge shares'}
          </Typography>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography sx={{ color: 'text.secondary', fontSize: '1.1rem', mb: 2 }}>
          {action === 'split'
            ? 'You must have USDC to split shares.'
            : 'Merge a share of Yes and No to get 1 USDC. Use this to reduce position cost.'}
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: '0.95rem', color: 'text.secondary', mb: 0.75 }}>
            Amount
          </Typography>
          <TextField
            fullWidth
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    size="small"
                    onClick={() => {
                      const maxValue = action === 'split' ? balance : minMergeAmount;
                      setAmount(maxValue > 0 ? maxValue.toString() : '0');
                    }}
                    sx={{ textTransform: 'none', minWidth: 'auto', px: 1 }}
                  >
                    Max
                  </Button>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {action === 'merge' && (
          <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mb: 1 }}>
            Available to merge: {minMergeAmount.toFixed(2)} shares
          </Typography>
        )}
        {isInvalidConditionId && (
          <Typography sx={{ fontSize: '0.8rem', color: 'error.main', mb: 1 }}>
            This market does not have a valid condition ID for split/merge.
          </Typography>
        )}
        {error && (
          <Typography sx={{ fontSize: '0.8rem', color: 'error.main', mb: 1 }}>{error}</Typography>
        )}

        <Button
          fullWidth
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || amountNum <= 0 || isInvalidConditionId}
          sx={{ py: 1.25, textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
        >
          {buttonText}
        </Button>
      </Paper>
    </Modal>
  );
}
